import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/lib/api'
import { formatCurrency } from '@/lib/utils'
import { FEATURE_KEYS, FEATURE_GROUPS } from '@/types/admin'
import { Check, X, Plus, Pencil } from 'lucide-react'
import type { Plan, CreatePlanPayload, UpdatePlanPayload } from '@/types/admin'

function PlanCard({
  plan,
  onEdit,
}: {
  plan: Plan
  onEdit: (plan: Plan) => void
}) {
  const planFeatureKeys = new Set(plan.plan_features.map((f) => f.feature_key))

  return (
    <div className={`rounded-lg border bg-card p-5 ${!plan.is_active ? 'opacity-60' : ''}`}>
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="font-semibold">{plan.name}</h3>
          {plan.description && (
            <p className="mt-0.5 text-xs text-muted-foreground">{plan.description}</p>
          )}
          <p className="mt-1 text-lg font-bold">
            {plan.price_idr != null ? formatCurrency(plan.price_idr) + '/bulan' : 'Custom'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!plan.is_active && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              Nonaktif
            </span>
          )}
          <button
            onClick={() => onEdit(plan)}
            className="inline-flex items-center gap-1 rounded-md border border-input px-2 py-1 text-xs hover:bg-muted"
          >
            <Pencil className="h-3 w-3" />
            Edit
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {FEATURE_GROUPS.map((group) => (
          <div key={group} className="space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group}
            </p>
            {FEATURE_KEYS.filter((f) => f.group === group).map(({ key, label }) => (
              <div key={key} className="flex items-center gap-2 text-sm">
                {planFeatureKeys.has(key) ? (
                  <Check className="h-4 w-4 shrink-0 text-green-600" />
                ) : (
                  <X className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span className={planFeatureKeys.has(key) ? '' : 'text-muted-foreground'}>
                  {label}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}

function PlanFormModal({
  plan,
  onClose,
  onSubmit,
  isPending,
  error,
}: {
  plan: Plan | null
  onClose: () => void
  onSubmit: (data: CreatePlanPayload | (UpdatePlanPayload & { id: string })) => void
  isPending: boolean
  error?: string
}) {
  const isEdit = !!plan
  const [name, setName] = useState(plan?.name ?? '')
  const [slug, setSlug] = useState(plan?.slug ?? '')
  const [description, setDescription] = useState(plan?.description ?? '')
  const [priceIdr, setPriceIdr] = useState<string>(
    plan?.price_idr != null ? String(plan.price_idr) : ''
  )
  const [isActive, setIsActive] = useState(plan?.is_active ?? true)
  const [selectedFeatures, setSelectedFeatures] = useState<Set<string>>(
    new Set(plan?.plan_features.map((f) => f.feature_key) ?? [])
  )

  function toggleFeature(key: string) {
    setSelectedFeatures((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const payload = {
      name,
      slug: slug.toLowerCase(),
      description: description || undefined,
      price_idr: priceIdr ? parseInt(priceIdr, 10) : null,
      features: Array.from(selectedFeatures),
      ...(isEdit ? { id: plan!.id, is_active: isActive } : {}),
    }
    onSubmit(payload as CreatePlanPayload | (UpdatePlanPayload & { id: string }))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg border bg-card p-6 shadow-lg">
        <h2 className="mb-4 text-lg font-semibold">
          {isEdit ? `Edit Plan: ${plan!.name}` : 'Buat Plan Baru'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Nama</label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Pro"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Slug</label>
              <input
                required
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                disabled={isEdit}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-60"
                placeholder="pro"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Deskripsi</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Deskripsi singkat plan ini..."
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Harga per Bulan (IDR)</label>
            <input
              type="number"
              value={priceIdr}
              onChange={(e) => setPriceIdr(e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              placeholder="Kosongkan jika custom/contact sales"
              min={0}
            />
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium">Fitur yang Termasuk</label>
            {FEATURE_GROUPS.map((group) => (
              <div key={group} className="space-y-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {group}
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {FEATURE_KEYS.filter((f) => f.group === group).map(({ key, label }) => (
                    <label
                      key={key}
                      className="flex cursor-pointer items-center gap-2 rounded-md border border-input px-3 py-2 text-sm hover:bg-muted"
                    >
                      <input
                        type="checkbox"
                        checked={selectedFeatures.has(key)}
                        onChange={() => toggleFeature(key)}
                        className="h-4 w-4 rounded"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {isEdit && (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded"
              />
              Plan aktif (tampil ke publik)
            </label>
          )}

          {error && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</p>
          )}

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? 'Menyimpan...' : 'Simpan'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-input px-4 py-2 text-sm hover:bg-muted"
            >
              Batal
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function PlansPage() {
  const qc = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null)

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: adminApi.getPlans,
  })

  const createPlan = useMutation({
    mutationFn: (data: CreatePlanPayload) => adminApi.createPlan(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'plans'] })
      setModalOpen(false)
    },
  })

  const updatePlan = useMutation({
    mutationFn: ({ id, ...data }: UpdatePlanPayload & { id: string }) =>
      adminApi.updatePlan(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'plans'] })
      setEditingPlan(null)
    },
  })

  function handleSubmit(data: CreatePlanPayload | (UpdatePlanPayload & { id: string })) {
    if ('id' in data) {
      const { id, ...rest } = data
      updatePlan.mutate({ id, ...rest })
    } else {
      createPlan.mutate(data as CreatePlanPayload)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Plan & Fitur</h1>
          <p className="text-sm text-muted-foreground">
            Kelola plan berlangganan dan bundle fitur untuk client.
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Buat Plan
        </button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-64 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onEdit={(p) => setEditingPlan(p)}
            />
          ))}
        </div>
      )}

      {(modalOpen || editingPlan) && (
        <PlanFormModal
          plan={editingPlan}
          onClose={() => {
            setModalOpen(false)
            setEditingPlan(null)
          }}
          onSubmit={handleSubmit}
          isPending={createPlan.isPending || updatePlan.isPending}
          error={createPlan.error?.message ?? updatePlan.error?.message}
        />
      )}
    </div>
  )
}
